import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import PlanIcon from 'presentations/views/components/atoms/PlanIcon';

import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Link from 'presentations/views/components/atoms/Link';
import Tooltip from 'presentations/views/components/atoms/Tooltip';
import Icon from 'presentations/views/components/atoms/Icon';
import HorizontalUserListItem from 'presentations/views/components/molecules/HorizontalUserListItem';
import { transformImageUrl } from 'presentations/assets';
import {
  black,
  gray01,
  gray02,
  gray04,
  gray05,
  gray06,
} from 'presentations/views/styles/color';
import Popover from 'presentations/views/components/atoms/Popover';
import { SyntheticEvent, useState, useContext } from 'react';
import { useRequest } from 'presentations/views/hooks/request';
import {
  accountSettingPath,
  adminPaymentPath,
  adminSettingPath,
  adminUserListPath,
  apiPath,
  dashboardPath,
  teamNewPath,
  teamPath,
  webPath,
} from 'presentations/routes/path';
import { TeamListItem } from 'presentations/api/schema/team';
import { ViewerItem } from 'presentations/api/schema/user';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { useRouter } from 'next/router';
import { post } from 'infras/rest_client';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import {
  setEvent,
  trackClickEvent,
} from 'presentations/views/helpers/analytics';
import { Role } from 'domains/models/team_user/role';

interface Props {
  viewer: ViewerItem;
  isHideExpandIcon?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, dispatch } = useContext(DashboardPortalContext);

  const [anchor, setAnchor] = useState<any>(null);
  const [loginTeam, setLoginTeam] = useState<TeamListItem | null>(null);
  const router = useRouter();

  const teamsResult = useRequest(apiPath(`/v1/user/teams`), {
    revalidateOnFocus: false,
  });
  const viewerTeam = props.viewer.team;
  const teams: TeamListItem[] = teamsResult.data || [];

  const onClick = (e: SyntheticEvent<HTMLDivElement>) => {
    e.preventDefault();
    setAnchor(e.currentTarget);
  };
  const onClose = () => {
    setAnchor(null);
  };

  const onClickTeam = async (team: TeamListItem) => {
    if (team.uid === props.viewer.team?.uid) return;
    setLoginTeam(team);
    const result = await post(apiPath(`/v1/teams/${team.uid}/login`));
    if (!result.isSuccess) {
      dispatch(setErrorMessage(t('login error')));
      setLoginTeam(null);
      return;
    }
    onClose();
    setEvent('click team on team menu', {
      viewerTeamId: props.viewer.team?.uid,
      selectTeamId: team.uid,
    });
    window.location.href = webPath(dashboardPath(), router.locale);
  };

  const onClickAddTeam = (e: SyntheticEvent<HTMLDivElement>) => {
    router.push(teamNewPath({ from: router.asPath }));
  };

  if (!viewerTeam) {
    return <Skeleton width={40} height={40} variant="circular" />;
  }
  const shortName = viewerTeam.name ? viewerTeam.name.charAt(0) : '';

  return (
    <>
      <Container onClick={onClick}>
        <UserAvatar
          src={transformImageUrl(viewerTeam.profileImage, {
            width: 40,
            height: 40,
            fill: 'crop',
          })}
          alt={viewerTeam.name || ''}
        >
          {shortName}
        </UserAvatar>
        {!props.isHideExpandIcon && <NaviIcon name="chevron-down" />}
      </Container>
      {anchor && (
        <Popover open anchor={anchor} onClose={onClose} maxWidth={320}>
          <PopoverContainer>
            <TeamListContainer>
              <TeamList>
                {teams.map((team) => (
                  <HorizontalUserListItem
                    key={team.uid}
                    userId={team.uid}
                    profileImage={team.profileImage}
                    onClick={() => onClickTeam(team)}
                    isActive={props.viewer.team?.uid === team.uid}
                    isLoading={loginTeam?.uid === team.uid}
                    name={team.name}
                    margin={8}
                  />
                ))}
                {teams.length < 4 && (
                  <AddButton onClick={onClickAddTeam}>
                    <AddTooltip title={t('Add')}>
                      <AddIcon name="plus" />
                    </AddTooltip>
                  </AddButton>
                )}
              </TeamList>
              {teams.length >= 4 && (
                <AddButton onClick={onClickAddTeam}>
                  <AddTooltip title={t('Add')}>
                    <AddIcon name="plus" />
                  </AddTooltip>
                </AddButton>
              )}
            </TeamListContainer>
            <TeamContainer>
              <TeamNameContainer>
                <AccountAvatar
                  src={transformImageUrl(viewerTeam.profileImage, {
                    width: 32,
                    height: 32,
                    dpr: 2,
                    fill: 'crop',
                  })}
                  alt={viewerTeam.name || ''}
                >
                  {shortName}
                </AccountAvatar>
                <TeamName>
                  <StyledPlanIcon
                    planId={props.viewer.planId}
                    planVersion={props.viewer.planVersion}
                  />
                  <TeamNameText>{viewerTeam.name}</TeamNameText>
                </TeamName>
              </TeamNameContainer>
              <TeamUrlContainer>
                <UrlIcon name="open-in-new" />
                <Url href={teamPath(viewerTeam.slug)} target="_blank">
                  {teamPath(viewerTeam.slug)}
                </Url>
              </TeamUrlContainer>
            </TeamContainer>
            {props.viewer.role <= Role.Admin && (
              <MenuList>
                <MenuListItem
                  onClick={trackClickEvent}
                  href={adminSettingPath()}
                  dataProps={{
                    ['data-event-name']: 'account menu click team setting',
                  }}
                >
                  <MenuIcon name="account-group" />
                  <MenuName>{t('team setting')}</MenuName>
                </MenuListItem>
                <MenuListItem
                  onClick={trackClickEvent}
                  href={adminUserListPath()}
                  dataProps={{
                    ['data-event-name']: 'account menu click members',
                  }}
                >
                  <MenuIcon name="account-cog" />
                  <MenuName>{t('member setting')}</MenuName>
                </MenuListItem>
                <MenuListItem
                  onClick={trackClickEvent}
                  href={adminPaymentPath()}
                  dataProps={{
                    ['data-event-name']: 'account menu click payment',
                  }}
                >
                  <MenuIcon name="list_alt_rounded" />
                  <MenuName>{t('Payment')}</MenuName>
                </MenuListItem>
              </MenuList>
            )}
            {/* <AccountContainer>
              <AccountNameContainer>
                <AccountAvatar
                  src={transformImageUrl(props.viewer.profileImage, {
                    width: 32,
                    height: 32,
                    fill: 'crop',
                    dpr: 2,
                  })}
                  alt={props.viewer.name || ''}
                >
                  {props.viewer.name.charAt(0)}
                </AccountAvatar>
                <AccountName>{props.viewer.name}</AccountName>
              </AccountNameContainer>
            </AccountContainer>
            <AccountMenuList>
              <MenuListItem
                href={accountSettingPath()}
                onClick={trackClickEvent}
                dataProps={{
                  ['data-event-name']: 'account menu click settings',
                }}
              >
                <MenuIcon name="settings" />
                <MenuName>{t('profile setting')}</MenuName>
              </MenuListItem>
            </AccountMenuList> */}
          </PopoverContainer>
        </Popover>
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const PopoverContainer = styled.div`
  padding: ${spacing(16)} 0;
`;
const TeamList = styled.div`
  display: flex;
  overflow-x: auto;
  flex: 1;
`;
const TeamContainer = styled.div`
  padding: 0 ${spacing(16)} ${spacing(16)};
  border-bottom: 1px solid ${gray05};
`;
const TeamNameContainer = styled.div`
  display: flex;
  align-items: center;
`;
const TeamName = styled.div`
  margin-left: ${spacing(8)};
  font-weight: ${fontWeight('bold')};
  width: 100%;
`;
const TeamNameText = styled.div`
  display: flex;
`;

const AccountContainer = styled.div`
  color: ${gray01};
  display: block;
  padding: ${spacing(16)} ${spacing(16)} ${spacing(16)};
  border-bottom: 1px solid ${gray05};
`;
const AccountNameContainer = styled.div`
  display: flex;
  align-items: center;
`;
const AccountName = styled.div`
  margin-left: ${spacing(8)};
  font-weight: ${fontWeight('bold')};
`;

const UserAvatar = styled(Avatar)`
  width: 32px;
  height: 32px;
  ${mediaMax.tablet} {
    width: 28px;
    height: 28px;
  }
`;
const AccountAvatar = styled(Avatar)`
  width: 32px;
  height: 32px;
`;

const NaviIcon = styled(Icon)`
  color: ${black};
  width: 14px !important;
  height: 14px !important;
  margin-left: ${spacing(2)};
`;
const TeamUrlContainer = styled.div`
  color: ${gray02};
  display: flex;
  align-items: center;
  margin-top: ${spacing(8)};
`;
const Url = styled(Link)`
  color: ${gray02};
  font-size: ${fontSize(12)};
`;
const UrlIcon = styled(Icon)`
  color: ${gray02};
  width: 14px !important;
  height: 14px !important;
  margin-right: ${spacing(4)};
`;

const MenuList = styled.div`
  /* border-bottom: 1px solid ${gray04}; */
  padding: ${spacing(4)} 0;
`;
const AccountMenuList = styled.div`
  padding: ${spacing(4)} 0 0;
`;
const MenuListItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${spacing(8)} ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
  :hover {
    background-color: ${gray06};
  }
`;

const MenuIcon = styled(Icon)`
  color: ${gray01};
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(12)};
`;
const MenuName = styled.div`
  color: ${gray01};
  font-weight: ${fontWeight('bold')};
`;

const AddIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${gray01};
`;
const AddTooltip = styled(Tooltip)`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const AddButton = styled.div`
  cursor: pointer;
  border: 1px dotted ${gray02};
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: ${spacing(8)};
  margin-top: ${spacing(2)};
`;

const TeamListContainer = styled.div`
  display: flex;
  justify-content: space-between;
  /* align-items: center; */
  padding: 0 ${spacing(16)} ${spacing(16)};
  margin-bottom: ${spacing(16)};
  border-bottom: 1px solid ${gray05};
`;

const StyledPlanIcon = styled(PlanIcon)`
  height: fit-content;
  flex-wrap: nowrap;
  white-space: nowrap;
`;
